import type { TabRequirement } from '@revelio/auth';
import { PagePaths } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import { RevelioPageId } from '../components/PageConfigProvider/types';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';

export type AnalyticsEntity =
  | 'company'
  | 'industry'
  | 'geography'
  | 'role'
  | 'skill'
  | 'activity';

export const ANALYTICS_ENTITIES: readonly AnalyticsEntity[] = [
  'company',
  'industry',
  'geography',
  'role',
  'skill',
  'activity',
];

/**
 * The analytics entity a page's primary filter stands for. A Record, not a
 * switch: a new `PrimaryFilterId` fails to compile until it is mapped here.
 * The header reads this to decide whether a CUSTOM page shows the analytics
 * tab strip, and a missing entry hides the strip silently — activity shipped
 * that way before this became exhaustive.
 */
const PRIMARY_FILTER_TO_ANALYTICS_ENTITY: Record<
  PrimaryFilterId,
  AnalyticsEntity
> = {
  [FilterId.COMPANY]: 'company',
  [FilterId.INDUSTRY]: 'industry',
  [FilterId.GEOGRAPHY]: 'geography',
  [FilterId.ROLE]: 'role',
  [FilterId.CUSTOM_SKILL]: 'skill',
  [FilterId.ACTIVITY]: 'activity',
};

export const getAnalyticsEntityForPrimaryFilter = (
  primaryFilterId: PrimaryFilterId | undefined
): AnalyticsEntity | undefined =>
  primaryFilterId === undefined
    ? undefined
    : PRIMARY_FILTER_TO_ANALYTICS_ENTITY[primaryFilterId];

export type AnalyticsView = {
  name: string;
  serverTab: TabRequirement;
  revelioPageIds: Partial<Record<AnalyticsEntity, RevelioPageId>>;
  paths: Partial<Record<AnalyticsEntity, PagePaths>>;
};

/**
 * The Analytics views available across all three primary entities (excludes
 * Screener which lives under Discovery, not Analytics). Order drives the
 * left-to-right order of the page-level tab strip. The bar holds an entity's
 * first four views (`useTabOrder`) — Compositions, Transitions, Job Postings,
 * then Sentiment for company or Market Conditions elsewhere — and the rest open
 * from the Pages menu. `serverTab` mirrors the
 * per-view route gating in routes.tsx so AnalyticsViewTabs can hide tabs the
 * user isn't entitled to — and `global-ai-assistant-access.ts` derives the AI
 * assistant's ONLY navigation entitlement gate from it, so loosening a
 * `serverTab` (e.g. to surface a tab for an upsell) also lets the assistant
 * navigate there.
 */
const COMPOSITIONS_VIEW: AnalyticsView = {
  name: 'Compositions',
  serverTab: Tab.Overview,
  revelioPageIds: {
    company: RevelioPageId.COMPANY_COMPOSITION,
    industry: RevelioPageId.INDUSTRY_COMPOSITION,
    geography: RevelioPageId.GEOGRAPHY_COMPOSITION,
    role: RevelioPageId.ROLE_COMPOSITION,
    skill: RevelioPageId.SKILL_COMPOSITION,
    activity: RevelioPageId.ACTIVITY_COMPOSITION,
  },
  paths: {
    company: PagePaths.COMPANY_COMPOSITIONS,
    industry: PagePaths.INDUSTRY_COMPOSITIONS,
    geography: PagePaths.GEOGRAPHY_COMPOSITIONS,
    role: PagePaths.ROLE_COMPOSITIONS,
    skill: PagePaths.SKILL_COMPOSITIONS,
    activity: PagePaths.ACTIVITY_COMPOSITIONS,
  },
};

export const ANALYTICS_VIEWS: readonly AnalyticsView[] = [
  COMPOSITIONS_VIEW,
  {
    name: 'Transitions',
    serverTab: Tab.Transition,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_TRANSITIONS,
      industry: RevelioPageId.INDUSTRY_TRANSITIONS,
      geography: RevelioPageId.GEOGRAPHY_TRANSITIONS,
      role: RevelioPageId.ROLE_TRANSITIONS,
    },
    paths: {
      company: PagePaths.COMPANY_TRANSITIONS,
      industry: PagePaths.INDUSTRY_TRANSITIONS,
      geography: PagePaths.GEOGRAPHY_TRANSITIONS,
      role: PagePaths.ROLE_TRANSITIONS,
    },
  },
  {
    name: 'Job Postings',
    serverTab: Tab.Posting,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_POSTINGS,
      industry: RevelioPageId.INDUSTRY_POSTINGS,
      geography: RevelioPageId.GEOGRAPHY_POSTINGS,
      role: RevelioPageId.ROLE_POSTINGS,
      skill: RevelioPageId.SKILL_POSTINGS,
      activity: RevelioPageId.ACTIVITY_POSTINGS,
    },
    paths: {
      company: PagePaths.COMPANY_POSTINGS,
      industry: PagePaths.INDUSTRY_POSTINGS,
      geography: PagePaths.GEOGRAPHY_POSTINGS,
      role: PagePaths.ROLE_POSTINGS,
      skill: PagePaths.SKILL_POSTINGS,
      activity: PagePaths.ACTIVITY_POSTINGS,
    },
  },
  {
    name: 'Market Conditions',
    serverTab: { allOf: [Tab.Overview, Tab.Posting] },
    revelioPageIds: {
      industry: RevelioPageId.INDUSTRY_MARKET_CONDITIONS,
      geography: RevelioPageId.GEOGRAPHY_MARKET_CONDITIONS,
      role: RevelioPageId.ROLE_MARKET_CONDITIONS,
      skill: RevelioPageId.SKILL_MARKET_CONDITIONS,
      activity: RevelioPageId.ACTIVITY_MARKET_CONDITIONS,
    },
    paths: {
      industry: PagePaths.INDUSTRY_MARKET_CONDITIONS,
      geography: PagePaths.GEOGRAPHY_MARKET_CONDITIONS,
      role: PagePaths.ROLE_MARKET_CONDITIONS,
      skill: PagePaths.SKILL_MARKET_CONDITIONS,
      activity: PagePaths.ACTIVITY_MARKET_CONDITIONS,
    },
  },
  {
    name: 'Sentiment',
    serverTab: Tab.Sentiment,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_SENTIMENT,
      industry: RevelioPageId.INDUSTRY_SENTIMENT,
      geography: RevelioPageId.GEOGRAPHY_SENTIMENT,
      role: RevelioPageId.ROLE_SENTIMENT,
    },
    paths: {
      company: PagePaths.COMPANY_SENTIMENT,
      industry: PagePaths.INDUSTRY_SENTIMENT,
      geography: PagePaths.GEOGRAPHY_SENTIMENT,
      role: PagePaths.ROLE_SENTIMENT,
    },
  },
  {
    name: 'Pay',
    serverTab: Tab.Compensation,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_PAY,
      industry: RevelioPageId.INDUSTRY_PAY,
      geography: RevelioPageId.GEOGRAPHY_PAY,
      role: RevelioPageId.ROLE_PAY,
      skill: RevelioPageId.SKILL_PAY,
      activity: RevelioPageId.ACTIVITY_PAY,
    },
    paths: {
      company: PagePaths.COMPANY_PAY,
      industry: PagePaths.INDUSTRY_PAY,
      geography: PagePaths.GEOGRAPHY_PAY,
      role: PagePaths.ROLE_PAY,
      skill: PagePaths.SKILL_PAY,
      activity: PagePaths.ACTIVITY_PAY,
    },
  },
  {
    name: 'Diversity',
    serverTab: Tab.Overview,
    revelioPageIds: {
      company: RevelioPageId.COMPANY_DIVERSITY,
    },
    paths: {
      company: PagePaths.COMPANY_DIVERSITY,
    },
  },
];

const OVERVIEW_VIEW: AnalyticsView = {
  name: 'Overview',
  serverTab: Tab.EntitySummary,
  revelioPageIds: {
    company: RevelioPageId.COMPANY_OVERVIEW,
    geography: RevelioPageId.GEOGRAPHY_OVERVIEW,
    role: RevelioPageId.ROLE_OVERVIEW,
  },
  paths: {
    company: PagePaths.COMPANY_SUMMARY,
    geography: PagePaths.GEOGRAPHY_SUMMARY,
    role: PagePaths.ROLE_SUMMARY,
  },
};

const COMPANY_SUBSIDIARIES_VIEW: AnalyticsView = {
  name: 'Corporate Structure',
  serverTab: Tab.Overview,
  revelioPageIds: {
    company: RevelioPageId.COMPANY_SUBSIDIARIES,
  },
  paths: {
    company: PagePaths.COMPANY_SUBSIDIARIES,
  },
};

/**
 * The views the header tab strip offers. Corporate Structure joins here rather
 * than in {@link ANALYTICS_VIEWS}: that list is also the AI assistant's
 * navigation gate and is checked against its page catalog, which has no
 * Corporate Structure page.
 */
export const TAB_STRIP_VIEWS: readonly AnalyticsView[] = [
  ...ANALYTICS_VIEWS,
  COMPANY_SUBSIDIARIES_VIEW,
];

const ALL_ANALYTICS_VIEWS: readonly AnalyticsView[] = [
  OVERVIEW_VIEW,
  ...TAB_STRIP_VIEWS,
];

export type EntityAnalyticsView = {
  name: string;
  serverTab: TabRequirement;
  revelioPageId: RevelioPageId;
  path: PagePaths;
};

export const getAnalyticsViewsForEntity = (
  views: readonly AnalyticsView[],
  entity: AnalyticsEntity
): EntityAnalyticsView[] =>
  views.flatMap((view) => {
    const revelioPageId = view.revelioPageIds[entity];
    const path = view.paths[entity];
    if (!revelioPageId || !path) return [];
    return [
      {
        name: view.name,
        serverTab: view.serverTab,
        revelioPageId,
        path,
      },
    ];
  });

const ANALYTICS_ENTITY_SET = new Set<string>(ANALYTICS_ENTITIES);

export const isAnalyticsEntity = (value: unknown): value is AnalyticsEntity =>
  typeof value === 'string' && ANALYTICS_ENTITY_SET.has(value);

export const resolveEntityLandingPath = (path: string): string => {
  const [entity, view] = path.split('/');
  if (view !== 'summary' || !isAnalyticsEntity(entity)) return path;
  return getAnalyticsViewsForEntity(ANALYTICS_VIEWS, entity)[0]?.path ?? path;
};

const ANALYTICS_REVELIO_PAGE_IDS = new Set<RevelioPageId>(
  ALL_ANALYTICS_VIEWS.flatMap((view) =>
    Object.values(view.revelioPageIds).filter(
      (id): id is RevelioPageId => id !== undefined
    )
  )
);

const REVELIO_PAGE_ID_TO_ANALYTICS_ENTITY: ReadonlyMap<
  RevelioPageId,
  AnalyticsEntity
> = new Map(
  ALL_ANALYTICS_VIEWS.flatMap((view) =>
    ANALYTICS_ENTITIES.flatMap((entity): [RevelioPageId, AnalyticsEntity][] => {
      const pageId = view.revelioPageIds[entity];
      return pageId ? [[pageId, entity]] : [];
    })
  )
);

export const isAnalyticsRevelioPage = (
  revelioPageId: RevelioPageId | undefined
): revelioPageId is RevelioPageId =>
  revelioPageId !== undefined && ANALYTICS_REVELIO_PAGE_IDS.has(revelioPageId);

export const getAnalyticsEntityFromRevelioPageId = (
  revelioPageId: RevelioPageId | undefined
): AnalyticsEntity | undefined =>
  revelioPageId
    ? REVELIO_PAGE_ID_TO_ANALYTICS_ENTITY.get(revelioPageId)
    : undefined;
